import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

const Wrap = styled.section`
  padding: 5rem 0;
  background: var(--bg-2);
  border-top: 1px solid var(--rule);
  border-bottom: 1px solid var(--rule);

  @media (min-width: 900px) { padding: 7rem 0; }
`;

const Inner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 1.5rem;

  @media (min-width: 768px) { padding: 0 2rem; }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  align-items: center;

  @media (min-width: 960px) {
    grid-template-columns: 0.85fr 1.15fr;
    gap: 4rem;
  }
`;

const Title = styled.h2`
  font-family: var(--sans);
  font-weight: 700;
  font-size: clamp(2.1rem, 4.4vw, 3.3rem);
  line-height: 1.04;
  letter-spacing: -0.025em;
  color: var(--ink);
  margin: 0 0 1.4rem;

  .accent {
    font-family: var(--serif);
    font-weight: 400;
    font-style: italic;
    letter-spacing: -0.012em;
    color: var(--accent);
  }
`;

const Body = styled.p`
  font-family: var(--sans);
  font-size: 1.05rem;
  line-height: 1.6;
  color: var(--ink-2);
  margin: 0;
  max-width: 46ch;
`;

/* ── Browser frame ────────────────────────────── */
const Browser = styled.div`
  background: var(--bg);
  border: 1px solid var(--rule);
  border-radius: 14px;
  padding: 12px;
  box-shadow:
    0 1px 0 rgba(14, 17, 22, 0.04),
    0 24px 60px -28px rgba(14, 17, 22, 0.18);
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;

  .dots { display: flex; gap: 5px; }
  .dot { width: 9px; height: 9px; border-radius: 50%; background: var(--rule-2); }
  .url {
    flex: 1;
    background: var(--bg-2);
    border: 1px solid var(--rule);
    color: var(--ink-3);
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.02em;
    padding: 6px 10px;
    border-radius: 8px;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/* ── Dashboard inside browser ──────────────────── */
const DashScroll = styled.div`
  background: var(--bg-2);
  border-radius: 8px;
  padding: 12px;
  max-height: 540px;
  overflow: hidden;
  position: relative;
`;

const DateBar = styled.div`
  background: var(--bg);
  border: 1px solid var(--rule);
  border-radius: 10px;
  padding: 10px 12px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 10px;

  .field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .label {
    font-family: var(--sans);
    font-size: 0.7rem;
    color: var(--ink-3);
    letter-spacing: -0.005em;
  }
  .val {
    font-family: var(--sans);
    font-size: 0.78rem;
    color: var(--ink);
    background: var(--bg-2);
    border: 1px solid var(--rule);
    border-radius: 6px;
    padding: 4px 8px;
    width: fit-content;
  }
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-bottom: 10px;

  @media (min-width: 560px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const KpiGridB = styled(KpiGrid)`
  grid-template-columns: repeat(2, 1fr);

  @media (min-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const KpiCard = styled.div`
  background: var(--bg);
  border: 1px solid var(--rule);
  border-radius: 10px;
  padding: 12px 14px 0;
  position: relative;
  overflow: hidden;

  .info {
    position: absolute;
    top: 8px;
    left: 10px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1px solid var(--rule-2);
    color: var(--ink-3);
    font-family: var(--sans);
    font-size: 8px;
    font-weight: 600;
    display: grid;
    place-items: center;
    line-height: 1;
  }

  .val {
    font-family: var(--sans);
    font-weight: 700;
    font-size: clamp(0.95rem, 1.6vw, 1.4rem);
    color: var(--ink);
    text-align: center;
    margin: 8px 0 6px;
    line-height: 1;
    letter-spacing: -0.02em;
  }

  .lbl {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-family: var(--sans);
    font-size: 0.62rem;
    color: ${(p) => p.$labelColor || "var(--ink-2)"};
    margin-bottom: 8px;
    line-height: 1.1;
    text-align: center;
  }

  .lbl svg { width: 10px; height: 10px; flex-shrink: 0; }

  .underline {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 2px;
    background: ${(p) => p.$accent || "var(--rule)"};
    opacity: 0.85;
  }
`;

const Table = styled.div`
  background: var(--bg);
  border: 1px solid var(--rule);
  border-radius: 10px;
  padding: 10px 12px;

  @media (max-width: 559px) { display: none; }

  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;

    .t {
      font-family: var(--sans);
      font-weight: 600;
      font-size: 0.8rem;
      color: var(--ink);
    }
    .btn {
      font-family: var(--sans);
      font-size: 0.65rem;
      font-weight: 500;
      color: var(--ink-2);
      background: var(--bg);
      border: 1px solid var(--rule-2);
      border-radius: 6px;
      padding: 4px 8px;
    }
  }

  .row {
    display: grid;
    grid-template-columns: 1.1fr 1.2fr 2fr 1.1fr;
    gap: 8px;
    padding: 6px 0;
    border-bottom: 1px solid var(--rule);
    font-family: var(--sans);
    font-size: 0.72rem;
    color: var(--ink-2);

    &:last-child { border-bottom: none; }
  }

  .row.h {
    color: var(--ink-3);
    font-size: 0.62rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    font-weight: 500;
    padding: 4px 0 8px;
    border-bottom: 1px solid var(--rule-2);
  }
`;

const ICONS = {
  money: (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 4.5v7M6 7c0-1 .9-1.5 2-1.5s2 .5 2 1.5-.9 1.4-2 1.5c-1.1.1-2 .5-2 1.5s.9 1.5 2 1.5 2-.5 2-1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  refresh: (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M13.5 8a5.5 5.5 0 11-2-4.2L13.5 5M13.5 2.5V5h-2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="6" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2 13c.6-2 2-3 4-3s3.4 1 4 3M10 7a2 2 0 100-4M14 13c-.4-1.5-1.3-2.3-2.6-2.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  bars: (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2 14V3M2 14h12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <rect x="4"  y="9" width="2" height="3" rx="0.5" stroke="currentColor" strokeWidth="1.4" />
      <rect x="8"  y="6" width="2" height="6" rx="0.5" stroke="currentColor" strokeWidth="1.4" />
      <rect x="12" y="10" width="2" height="2" rx="0.5" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  ),
};

export default function Dashboard() {
  const G = "var(--success-2)";
  const B = "var(--info)";

  return (
    <Wrap id="dashboard">
      <Inner>
        <Layout>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <Title>
              Decisiones basadas en <span className="accent">data</span>
            </Title>
            <Body>
              Cada conversación deja un rastro. Analizamos respuestas, recompras
              y reactivaciones en un panel online y siempre accesible.
            </Body>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <Browser>
              <Bar>
                <div className="dots">
                  <span className="dot" />
                  <span className="dot" />
                  <span className="dot" />
                </div>
                <div className="url">panel.clienterecurrente.cl</div>
              </Bar>

              <DashScroll>
                <DateBar>
                  <div className="field">
                    <span className="label">Fecha de inicio</span>
                    <span className="val">may 8, 2026</span>
                  </div>
                  <div className="field">
                    <span className="label">Fecha de fin</span>
                    <span className="val">jun 7, 2026</span>
                  </div>
                </DateBar>

                <KpiGrid>
                  <KpiCard $accent={G} $labelColor={G}>
                    <span className="info">i</span>
                    <div className="val">$ 2.147.881</div>
                    <div className="lbl">Total recompra efectiva {ICONS.money}</div>
                    <span className="underline" />
                  </KpiCard>
                  <KpiCard $accent={G} $labelColor={G}>
                    <span className="info">i</span>
                    <div className="val">62</div>
                    <div className="lbl">Recompras efectivas {ICONS.money}</div>
                    <span className="underline" />
                  </KpiCard>
                  <KpiCard $accent={G} $labelColor={G}>
                    <span className="info">i</span>
                    <div className="val">16,9%</div>
                    <div className="lbl">Porcentaje de recompra {ICONS.refresh}</div>
                    <span className="underline" />
                  </KpiCard>
                </KpiGrid>

                <KpiGridB>
                  <KpiCard $accent={B} $labelColor={B}>
                    <span className="info">i</span>
                    <div className="val">40</div>
                    <div className="lbl">Suscritos a recordatorios {ICONS.users}</div>
                    <span className="underline" />
                  </KpiCard>
                  <KpiCard $accent={B} $labelColor={B}>
                    <span className="info">i</span>
                    <div className="val">8,40%</div>
                    <div className="lbl">Porcentaje de suscritos {ICONS.bars}</div>
                    <span className="underline" />
                  </KpiCard>
                </KpiGridB>

                <Table>
                  <div className="head">
                    <span className="t">Recordatorios de compras programados</span>
                    <span className="btn">Exportar tabla</span>
                  </div>
                  <div className="row h">
                    <span>Cliente</span>
                    <span>Celular</span>
                    <span>Productos</span>
                    <span>Próximo</span>
                  </div>
                  <div className="row">
                    <span>Yanara</span>
                    <span>56923847102</span>
                    <span>(1) Bravery salmon puppy 7 kg</span>
                    <span>25/05/2026</span>
                  </div>
                  <div className="row">
                    <span>Cinthya</span>
                    <span>56956123890</span>
                    <span>(2) Nomade raza pequeña 10 kg</span>
                    <span>27/05/2026</span>
                  </div>
                  <div className="row">
                    <span>Jorge</span>
                    <span>56934785621</span>
                    <span>(2) Mastin adulto raza pequeña 10 kg</span>
                    <span>08/06/2026</span>
                  </div>
                  <div className="row">
                    <span>Constanza</span>
                    <span>56971590348</span>
                    <span>(2) Arena practicat multicat 14 kg</span>
                    <span>08/06/2026</span>
                  </div>
                </Table>
              </DashScroll>
            </Browser>
          </motion.div>
        </Layout>
      </Inner>
    </Wrap>
  );
}
